<script setup lang="ts"></script>
<template>
    <ContentPage title="Заявление об доступности Fleequid">
        <section class="mb-8">
            <p class="mt-3 text-base">
                Fleequid (Adorea SRL). берет на себя обязательство сделать свой веб-сайт доступным в соответствии с законом от 9 января 2004 года, №
                4. Данное заявление об доступности применяется к:
            </p>
            <p class="mt-2">
                <NuxtLink class="text-primary hover:underline" to="https://fleequid.com" target="_blank" external>https://fleequid.com</NuxtLink>
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Доступность</h2>
            <p class="mt-2">
                Наша цель — сделать цифровой контент доступным для всех людей, включая людей с ограниченными возможностями. Мы стремимся постоянно
                улучшать пользовательский опыт и применять лучшие практики в области доступности, чтобы обеспечить равный и инклюзивный доступ.
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Заявление о доступности</h2>
            <p class="mt-2">
                В соответствии с действующим законодательством и нашими ценностями инклюзии и равных возможностей мы подготовили официальное Заявление
                о доступности. В документе описывается уровень доступности сайта и меры, предпринятые для соблюдения Web Content Accessibility
                Guidelines (WCAG). Полный текст Заявления доступен для ознакомления и скачивания по ссылке ниже:
            </p>
            <p class="mt-2">
                <a class="text-primary hover:underline" href="/docs/accessibility/accessibility-statement_RU.pdf" target="_blank">
                    Заявление об доступности Fleequid
                </a>
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Состояние соблюдения требований</h2>
            <p class="mt-2">Частично соответствует</p>
            <p class="mt-3">
                Этот веб-сайт частично соответствует требованиям, предусмотренным приложением A стандарта UNI CEI EN 301549, по причине случаев
                несоответствия, перечисленных ниже.
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Недоступные материалы</h2>
            <p class="mt-3">Материалы, перечисленные ниже, недоступны из-за несоответствия закону от 9 января 2004 года, № 4.:</p>

            <h3 class="text-highlighted mt-5 text-lg font-semibold">Для веб-страниц</h3>
            <h4 class="text-highlighted mt-3 font-medium">В общем:</h4>
            <ul class="mt-2 list-disc space-y-1 pl-6">
                <li>1.3.1 Информация и связи (Уровень A)</li>
                <li>1.4.3 Минимальный контраст (Уровень AA)</li>
                <li>1.4.4 Масштабирование текста (Уровень AA)</li>
                <li>1.4.10 Пересчет потока (только уровень AA 2.1)</li>
                <li>2.4.1 Пропуск блоков (Уровень A)</li>
                <li>2.4.4 Цель ссылки (в контексте) (Уровень A)</li>
                <li>2.4.5 Разные способы (Уровень AA)</li>
                <li>3.3.1 Идентификация ошибок (Уровень A)</li>
                <li>4.1.2 Имя, роль, значение (Уровень A)</li>
                <li>4.1.3 Сообщения о статусе (Уровень AA wcag 2.1)</li>
            </ul>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Редакция заявления об доступности</h2>
            <p class="mt-3">Данное заявление было составлено <time datetime="2025-08-20" class="font-bold">20 августа 2025 года</time>.</p>
            <p class="mt-2">
                Информация, содержащаяся в данном заявлении об доступности, получена на основании оценки, проведенной третьими сторонами, с
                использованием систем автоматической валидации и путем прямой проверки, выполненной вручную техническим экспертом по доступности.
            </p>

            <h3 class="text-highlighted mt-5 text-lg font-semibold">Инструменты, используемые для автоматической валидации:</h3>
            <ul class="mt-2 list-disc space-y-1 pl-6">
                <li>Deque Axe Accessibility Checker (расширения для Firefox и Chrome)</li>
                <li>Siteimprove Accessibility Checker</li>
                <li>WCAG color contrast checker</li>
            </ul>

            <h3 class="text-highlighted mt-5 text-lg font-semibold">Ассистивные технологии:</h3>
            <ul class="mt-2 list-disc space-y-1 pl-6">
                <li>Читалки с экраном: NVDA</li>
            </ul>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Обратная связь и контактная информация</h2>
            <p class="mt-3">Вы можете отправить любые сообщения по электронной почте: <FleequidEmailLink type="accessibility" /></p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Способы отправки сообщений в AgID</h2>
            <p class="mt-3">
                В случае неудовлетворительного ответа или отсутствия ответа в течение тридцати дней на уведомление или запрос, заинтересованное лицо
                может направить сообщение, используя способ, указанный на официальном сайте AgID.
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Информация о сайте</h2>
            <dl class="mt-4 space-y-4">
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">Дата публикации веб-сайта:</dt>
                    <dd class="sm:col-span-2">
                        <time datetime="2024-08-27" class="font-bold">27 августа 2024 года</time>
                    </dd>
                </div>
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">Проводились тесты на удобство использования:</dt>
                    <dd class="font-bold sm:col-span-2">нет</dd>
                </div>
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">CMS, использованная для веб-сайта:</dt>
                    <dd class="font-bold sm:col-span-2">Собственная/custom-платформа, разработанная внутренне</dd>
                </div>
            </dl>
        </section>

        <section class="mb-4">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Информация о структуре</h2>
            <dl class="mt-4 space-y-4">
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">Количество сотрудников с ограниченными возможностями, работающих в администрации:</dt>
                    <dd class="font-bold sm:col-span-2">1</dd>
                </div>
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">Количество рабочих мест для сотрудников с ограниченными возможностями:</dt>
                    <dd class="font-bold sm:col-span-2">0</dd>
                </div>
            </dl>
        </section>
    </ContentPage>
</template>
